import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Box, MenuItem, TextField } from '@mui/material';
import { COUNTRIES } from '../config/countries';
import {
  DEFAULT_PHONE_REGION,
  DIAL_CODES,
  splitInternationalPhone,
  toInternationalPhone,
} from '../config/phoneRegions';

/**
 * Ülke kodu seçici + yerel numara alanı. Tek bir string ile çalışır: dışarıya her zaman
 * E.164 birleşik hâli verir (ör. "+905388828249"), yerel numara boşsa "". Böylece mevcut
 * tek-alanlı `phone` form alanlarına ve zod `.min(1)` zorunluluk kurallarına dokunmadan
 * takılır; telefon backend/sağlayıcı isteklerine `+` önekli gider.
 *
 * `<Controller>` ile kullanım:
 *   <Controller name="phone" control={control} render={({ field }) => (
 *     <PhoneField value={field.value} onChange={field.onChange} onBlur={field.onBlur}
 *       label="Telefon" error={!!errors.phone} helperText={errors.phone?.message} />
 *   )} />
 */
export interface PhoneFieldProps {
  value: string | undefined | null;
  onChange: (combined: string) => void;
  onBlur?: () => void;
  label?: string;
  error?: boolean;
  helperText?: ReactNode;
  disabled?: boolean;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  /** Numara girişine verilecek placeholder. */
  placeholder?: string;
}

const REGION_OPTIONS = COUNTRIES.filter((c) => DIAL_CODES[c.code]).map((c) => ({
  code: c.code,
  name: c.name,
  dial: DIAL_CODES[c.code],
}));

export function PhoneField({
  value,
  onChange,
  onBlur,
  label = 'Telefon',
  error,
  helperText,
  disabled,
  size = 'small',
  fullWidth = true,
  placeholder = '5XX XXX XX XX',
}: PhoneFieldProps) {
  const initial = splitInternationalPhone(value);
  const [region, setRegion] = useState(initial.region || DEFAULT_PHONE_REGION);
  const [local, setLocal] = useState(initial.local);

  // Dışarıdan gelen değer (ön-doldurma / reset) yerel duruma uymuyorsa senkronla ve
  // gerekirse normalize edilmiş (`+` önekli) hâlini forma geri yaz.
  const lastEmitted = useRef(toInternationalPhone(region, local));
  useEffect(() => {
    const incoming = (value ?? '').trim();
    if (incoming === lastEmitted.current) return;
    const next = splitInternationalPhone(incoming);
    setRegion(next.region || DEFAULT_PHONE_REGION);
    setLocal(next.local);
    const normalized = toInternationalPhone(next.region, next.local);
    lastEmitted.current = normalized;
    if (normalized !== incoming) onChange(normalized);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const emit = (nextRegion: string, nextLocal: string) => {
    const combined = toInternationalPhone(nextRegion, nextLocal);
    lastEmitted.current = combined;
    onChange(combined);
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, width: fullWidth ? '100%' : undefined }}>
      <TextField
        select
        size={size}
        label="Ülke"
        value={region}
        disabled={disabled}
        onChange={(e) => {
          setRegion(e.target.value);
          emit(e.target.value, local);
        }}
        sx={{ flex: '0 0 auto', minWidth: 116 }}
        slotProps={{
          select: { renderValue: (v) => `${DIAL_CODES[String(v)] ?? ''}` },
        }}
      >
        {REGION_OPTIONS.map((o) => (
          <MenuItem key={o.code} value={o.code}>
            {o.name} ({o.dial})
          </MenuItem>
        ))}
      </TextField>
      <TextField
        size={size}
        fullWidth
        label={label}
        placeholder={placeholder}
        value={local}
        disabled={disabled}
        error={error}
        helperText={helperText}
        onBlur={onBlur}
        onChange={(e) => {
          const digits = e.target.value.replace(/[^\d]/g, '');
          setLocal(digits);
          emit(region, digits);
        }}
        slotProps={{ inputLabel: { shrink: true }, htmlInput: { inputMode: 'tel', maxLength: 15 } }}
      />
    </Box>
  );
}
